import { Theme } from '@emotion/react';

const darkFeatureGraph: Theme['featureGraph'] = {
  interface: {
    background: 'rgba(143, 89, 255, 0.06)',
    border: 'rgba(143, 89, 255, 0.3)',
    accent: '#b793ff',
    muted: '#8e7ba9'
  },
  cloudService: {
    background: 'rgba(72, 150, 220, 0.06)',
    border: 'rgba(100, 175, 235, 0.3)',
    accent: '#8fc7f2',
    muted: '#7497b2'
  },
  runtime: {
    background: 'rgba(221, 119, 152, 0.06)',
    border: 'rgba(221, 119, 152, 0.3)',
    accent: '#e9a2b9',
    muted: '#ac8190'
  },
  tool: {
    background: 'rgba(218, 157, 62, 0.06)',
    border: 'rgba(218, 157, 62, 0.3)',
    accent: '#e8bd77',
    muted: '#ad9470'
  },
  feature: { background: '#191f24', border: 'rgba(93, 195, 187, 0.25)', accent: '#7fd4cc', muted: '#6b9e99' }
};

const lightFeatureGraph: Theme['featureGraph'] = {
  interface: {
    background: 'rgba(143, 89, 255, 0.04)',
    border: 'rgba(116, 64, 203, 0.25)',
    accent: '#7440cb',
    muted: '#857099'
  },
  cloudService: {
    background: 'rgba(50, 120, 180, 0.04)',
    border: 'rgba(40, 105, 160, 0.25)',
    accent: '#2869a0',
    muted: '#617e96'
  },
  runtime: {
    background: 'rgba(168, 62, 100, 0.04)',
    border: 'rgba(168, 62, 100, 0.25)',
    accent: '#a83e64',
    muted: '#976e7c'
  },
  tool: {
    background: 'rgba(180, 120, 25, 0.04)',
    border: 'rgba(150, 95, 12, 0.25)',
    accent: '#88570f',
    muted: '#8b7657'
  },
  feature: { background: '#f7fbfa', border: 'rgba(28, 115, 108, 0.22)', accent: '#1c736c', muted: '#5c817c' }
};

export const DarkTheme: Theme = {
  light: false,
  notFound: { accent: '#f07979' },
  featureGraph: darkFeatureGraph,
  selectionPill: {
    tones: {
      ...darkFeatureGraph,
      insights: {
        background: 'rgba(151, 164, 184, 0.12)',
        border: 'rgba(151, 164, 184, 0.34)',
        accent: '#bdc9d9',
        muted: '#8d9aaa'
      }
    },
    unselected: {
      color: '#bbbbdb',
      background: 'transparent',
      border: 'rgba(255, 255, 255, 0.08)'
    },
    checkColor: 'rgb(16, 16, 20)'
  },
  layout: {
    background: 'rgb(16, 16, 20)',
    fade: 'linear-gradient(rgba(16, 16, 20, 0.8), rgb(16, 16, 20))'
  },
  section: {
    imageShadow1: '#7524ff',
    imageShadow2: '#ff245e',
    imageShadow3: '#ff8724'
  },
  mobileDropdownMenu: {
    height: 45
  },
  header: {
    height: 60,
    background: `rgba(16, 16, 20, 0.7)`,
    textColor: 'white',
    dropdownBackground: 'rgb(16, 16, 20)',
    dropdownShadow: '0 0 20px rgba(0, 0, 0, 0.5)',
    dropdownBorder: 'rgba(255, 255, 255, 0.2)',
    dropdownHover: 'rgba(143, 89, 255, 0.18)',
    dropdownSelected: 'linear-gradient(30deg, #435785, #451c70)',
    searchBackground: 'rgba(28, 27, 35, 0.88)',
    searchBorder: 'rgba(255, 255, 255, 0.12)',
    searchPlaceholder: 'rgba(255, 255, 255, 0.5)',
    searchHighlightBackground: 'rgba(143, 89, 255, 0.24)',
    searchHighlightColor: '#d7c7ff'
  },
  tagBadge: {
    default: {
      background: 'rgba(60, 208, 51, 0.18)',
      border: 'transparent',
      color: '#98ff2f'
    },
    new: {
      background: 'rgba(28, 190, 222, 0.28)',
      border: 'rgba(67, 211, 237, 0.5)',
      color: '#a8f2ff'
    },
    muted: {
      background: 'rgba(187, 187, 219, 0.14)',
      border: 'rgba(214, 214, 232, 0.12)',
      color: 'rgba(214, 214, 232, 0.88)'
    },
    danger: {
      background: 'rgba(255, 84, 84, 0.18)',
      border: 'transparent',
      color: '#ff8a8a'
    }
  },
  hero: {
    background: 'rgba(0, 0, 0, 0.4)'
  },
  footer: {
    background: `rgb(24, 24, 30)`,
    textColor: `#585867`,
    textHoverColor: `#ffffff`,
    textFontSize: 13
  },
  card: {
    background: '#1c1b23',
    hoverBackground: 'rgba(143, 89, 255, 0.16)',
    radius: '20px',
    shadow: '0 14px 30px rgba(0, 0, 0, 0.18)'
  },
  surface: {
    background: 'rgba(24, 25, 32, 0.94)',
    border: 'rgba(255, 255, 255, 0.08)',
    shadow: '0 18px 44px rgba(0, 0, 0, 0.24)',
    radius: '24px'
  },
  docsCallout: {
    info: {
      background: 'linear-gradient(135deg, rgba(143, 89, 255, 0.12), rgba(24, 24, 30, 0.96))',
      borderAccent: '#8f59ff',
      pillBackground: 'rgba(143, 89, 255, 0.18)',
      pillBorder: 'rgba(143, 89, 255, 0.28)',
      pillText: '#d7c7ff',
      quoteColor: 'rgba(143, 89, 255, 0.22)'
    },
    enterprise: {
      background: 'linear-gradient(135deg, rgba(60, 208, 51, 0.1), rgba(24, 24, 30, 0.96))',
      borderAccent: '#3cd033',
      pillBackground: 'rgba(60, 208, 51, 0.18)',
      pillBorder: 'rgba(60, 208, 51, 0.28)',
      pillText: '#98ff2f',
      quoteColor: 'rgba(60, 208, 51, 0.2)'
    },
    versionNote: {
      background: 'linear-gradient(135deg, rgba(65, 154, 190, 0.11), rgba(24, 24, 30, 0.96))',
      borderAccent: '#5baecb',
      pillBackground: 'rgba(91, 174, 203, 0.15)',
      pillBorder: 'rgba(91, 174, 203, 0.28)',
      pillText: '#a8d9e9',
      quoteColor: 'rgba(91, 174, 203, 0.2)'
    },
    warning: {
      background: 'linear-gradient(135deg, rgba(240, 168, 76, 0.13), rgba(33, 28, 25, 0.96))',
      borderAccent: '#f0a84c',
      pillBackground: 'rgba(240, 168, 76, 0.16)',
      pillBorder: 'rgba(240, 168, 76, 0.24)',
      pillText: '#ffd9a2',
      quoteColor: 'rgba(240, 168, 76, 0.2)'
    }
  },
  text: {
    special: '#8f59ff',
    special2: '#3cd033',
    font: 'Inter',
    fontFallback: 'sans-serif',
    heading: `#ffffff`,
    paragraph: '#bbbbdb',
    referenceDescription: '#9292b3',
    listBackground: 'rgb(24, 24, 30)',
    listBorder: 'rgb(52, 52, 65)',
    listRadius: '10px',
    tableBackground: '#181920',
    tableBorder: 'rgba(255, 255, 255, 0.12)',
    tableHeaderBackground: '#23252f',
    tableHeaderText: '#f3f3f7',
    tableRowOdd: 'rgba(255, 255, 255, 0.02)',
    tableRowEven: 'rgba(255, 255, 255, 0.045)',
    blockquoteBackground: 'linear-gradient(135deg, rgba(143, 89, 255, 0.12), rgba(24, 24, 30, 0.96))',
    blockquoteShadow: '0 18px 44px rgba(0, 0, 0, 0.24)',
    blockquoteQuoteColor: 'rgba(143, 89, 255, 0.22)',
    codeInlineBackground: 'linear-gradient(180deg, rgba(61, 61, 81, 0.92), rgba(46, 46, 59, 0.92))',
    codeInlineBorder: 'rgba(255, 255, 255, 0.08)',
    codeInlineShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.05)',
    codeBackground: 'rgb(46,46,59)',
    codeBlockBackground: '#1d1c27',
    codeBlockBorder: 'rgba(143, 89, 255, 0.16)',
    codeBlockShadow: '0 24px 60px rgba(0, 0, 0, 0.28)',
    mediaShadow: '0 0 40px rgba(0, 0, 0, 0.5)',
    codeColor: '#ffffff'
  },
  scrollbar: {
    trackBackground: 'rgba(255, 255, 255, 0.12)',
    thumbBackground: 'rgb(118, 118, 145)',
    thumbBorder: 'rgb(157, 157, 190)'
  },
  pricing: {
    background0: '#8f59ff',
    background1: '#3478f6',
    background2: '#19702b',
    background3: '#1594b5',
    checkIcon0: '#8f59ff',
    checkIcon1: '#69a0ff',
    checkIcon2: '#3cd033',
    checkIcon3: '#39c9e7'
  },
  home: {
    walkthroughButton: {
      background: '#349ff629',
      hoverBackground: '#349ff640',
      border: '#69bbff',
      icon: '#69bbff',
      focus: '#69bbff'
    }
  }
};

export const LightTheme: Theme = {
  light: true,
  notFound: { accent: '#c23d3d' },
  featureGraph: lightFeatureGraph,
  selectionPill: {
    tones: {
      ...lightFeatureGraph,
      insights: {
        background: 'rgba(80, 99, 124, 0.1)',
        border: 'rgba(80, 99, 124, 0.28)',
        accent: '#50637c',
        muted: '#73849a'
      }
    },
    unselected: {
      color: '#575766',
      background: 'transparent',
      border: '#dde1e6'
    },
    checkColor: '#f1f3f5'
  },
  layout: {
    background: '#f1f3f5',
    fade: 'linear-gradient(rgba(241, 243, 245, 0.8), rgb(241, 243, 245))'
  },
  card: {
    background: '#f5f6f7',
    hoverBackground: 'rgba(143, 89, 255, 0.1)',
    radius: '20px',
    shadow: '0 6px 20px rgba(54, 45, 82, 0.1)'
  },
  surface: {
    background: 'rgba(255, 255, 255, 0.96)',
    border: '#dde1e6',
    shadow: '0 10px 28px rgba(54, 45, 82, 0.1)',
    radius: '24px'
  },
  docsCallout: {
    info: {
      background: 'linear-gradient(135deg, #ffffff, #f5f6f8)',
      borderAccent: '#7f6cff',
      pillBackground: 'rgba(127, 108, 255, 0.14)',
      pillBorder: 'rgba(127, 108, 255, 0.28)',
      pillText: '#5d47df',
      quoteColor: 'rgba(143, 89, 255, 0.18)'
    },
    enterprise: {
      background: 'linear-gradient(135deg, #f1fbf0, #ffffff)',
      borderAccent: '#278536',
      pillBackground: 'rgba(39, 133, 54, 0.1)',
      pillBorder: 'rgba(39, 133, 54, 0.22)',
      pillText: '#1f6e2c',
      quoteColor: 'rgba(39, 133, 54, 0.14)'
    },
    versionNote: {
      background: 'linear-gradient(135deg, #f5fbfd, #eef6f8)',
      borderAccent: '#398eaa',
      pillBackground: 'rgba(57, 142, 170, 0.1)',
      pillBorder: 'rgba(57, 142, 170, 0.25)',
      pillText: '#286b82',
      quoteColor: 'rgba(57, 142, 170, 0.16)'
    },
    warning: {
      background: 'linear-gradient(135deg, #fff9ee, #fdf1df)',
      borderAccent: '#d68a2c',
      pillBackground: 'rgba(214, 138, 44, 0.12)',
      pillBorder: 'rgba(214, 138, 44, 0.24)',
      pillText: '#a86410',
      quoteColor: 'rgba(214, 138, 44, 0.18)'
    }
  },
  hero: {
    background: 'rgba(255,255,255, 0.8)'
  },
  section: {
    imageShadow1: '#1a0838',
    imageShadow2: '#360814',
    imageShadow3: '#311a08'
  },
  mobileDropdownMenu: {
    height: 45
  },
  header: {
    height: 60,
    background: `rgb(255 255 255 / 85%)`,
    textColor: 'black',
    dropdownBackground: '#ffffff',
    dropdownShadow: '0 8px 24px rgba(24, 32, 44, 0.1)',
    dropdownBorder: '#dde1e6',
    dropdownHover: '#f0ecfa',
    dropdownSelected: 'linear-gradient(30deg, #a2befd, #a249ff)',
    searchBackground: 'rgba(255, 255, 255, 0.88)',
    searchBorder: '#d9dee5',
    searchPlaceholder: 'rgba(87, 87, 102, 0.7)',
    searchHighlightBackground: 'rgba(127, 108, 255, 0.16)',
    searchHighlightColor: '#5d47df'
  },
  tagBadge: {
    default: {
      background: 'rgba(255, 145, 41, 0.2)',
      border: 'transparent',
      color: '#ff7a00'
    },
    new: {
      background: 'rgba(0, 153, 190, 0.18)',
      border: 'rgba(0, 132, 166, 0.34)',
      color: '#006b86'
    },
    muted: {
      background: 'rgba(112, 118, 134, 0.16)',
      border: 'rgba(112, 118, 134, 0.18)',
      color: 'rgba(71, 78, 94, 0.92)'
    },
    danger: {
      background: 'rgba(220, 38, 38, 0.12)',
      border: 'transparent',
      color: '#b91c1c'
    }
  },
  footer: {
    background: `rgb(255 255 255 / 85%)`,
    textColor: `#1f1f25`,
    textHoverColor: `#ffffff`,
    textFontSize: 13
  },
  text: {
    special: '#8f59ff',
    special2: '#00b350',
    font: 'Inter',
    fontFallback: 'sans-serif',
    heading: `#000000`,
    paragraph: '#575766',
    referenceDescription: '#4e4e5e',
    listBackground: '#f5f6f7',
    listBorder: '#d9dee5',
    listRadius: '10px',
    tableBackground: '#ffffff',
    tableBorder: '#d9dee5',
    tableHeaderBackground: '#f0f2f4',
    tableHeaderText: '#232637',
    tableRowOdd: 'rgba(255,255,255, 0.78)',
    tableRowEven: 'rgba(32, 36, 55, 0.04)',
    blockquoteBackground: 'linear-gradient(135deg, #ffffff, #f5f6f8)',
    blockquoteShadow: '0 10px 28px rgba(54, 45, 82, 0.1)',
    blockquoteQuoteColor: 'rgba(143, 89, 255, 0.18)',
    codeInlineBackground: 'linear-gradient(180deg, #f4f1fb, #ebe6f5)',
    codeInlineBorder: 'rgba(143, 89, 255, 0.2)',
    codeInlineShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.55)',
    codeBackground: '#f3f4f6',
    codeBlockBackground: '#f6f7f8',
    codeBlockBorder: '#d9dee5',
    codeBlockShadow: '0 5px 16px rgba(54, 45, 82, 0.08)',
    mediaShadow: '0 8px 24px rgba(54, 45, 82, 0.18)',
    codeColor: '#434561'
  },
  scrollbar: {
    trackBackground: 'rgba(177, 177, 220, 0.3)',
    thumbBackground: '#FFFFFF',
    thumbBorder: '#B1B1DCFF'
  },
  pricing: {
    background0: '#8f59ff',
    background1: '#2563eb',
    background2: '#278536',
    background3: '#007f9e',
    checkIcon0: '#8f59ff',
    checkIcon1: '#2563eb',
    checkIcon2: '#278536',
    checkIcon3: '#007f9e'
  },
  home: {
    walkthroughButton: {
      background: 'rgba(37, 99, 235, 0.08)',
      hoverBackground: 'rgba(37, 99, 235, 0.14)',
      border: '#2563eb',
      icon: '#2563eb',
      focus: '#2563eb'
    }
  }
};
